import { ProtectedRoute } from "@/components/protected-route";
import { Outlet, Link, useLocation, useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import { useSession } from "@/services/auth";
import { marketingUrl } from "@/services/marketing";
import { usePaymentMethods } from "@/api/use-payment-methods-api";
import { useUnpaidInvoices } from "@/api/use-invoices";
import { LanguageSwitcher } from "@/components/language-switcher";
import {
  Avatar,
  Button,
  Card,
  CardBody,
  Chip,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownTrigger,
  Navbar,
  NavbarBrand,
  NavbarContent,
  NavbarItem,
} from "@heroui/react";
import {
  AlertTriangle,
  Bell,
  ChevronDown,
  CreditCard,
  FileText,
  Info,
  AlertCircle,
  ExternalLink,
  LifeBuoy,
  LogOut,
  ShieldAlert,
  Ticket,
  User,
} from "lucide-react";
import {
  useActiveAnnouncements,
  type AnnouncementType,
} from "@/api/use-announcements";

const allNavItems = [
  { titleKey: "nav.shipments", url: "/dashboard" },
  { titleKey: "nav.invoices", url: "/invoices" },
  { titleKey: "nav.addressBook", url: "/my-address-book" },
  { titleKey: "nav.myPackaging", url: "/my-packaging" },
  { titleKey: "nav.carriers", url: "/carriers" },
];

const announcementConfig: Record<
  AnnouncementType,
  {
    color: "primary" | "warning" | "danger";
    icon: typeof Info;
    iconClass: string;
    cardClass: string;
    textClass: string;
    subTextClass: string;
  }
> = {
  INFO: {
    color: "primary",
    icon: Info,
    iconClass: "text-primary",
    cardClass: "border border-primary/20 bg-primary/5",
    textClass: "text-primary",
    subTextClass: "text-primary/80",
  },
  WARNING: {
    color: "warning",
    icon: AlertTriangle,
    iconClass: "text-warning-600",
    cardClass: "border border-warning-200 bg-warning-50",
    textClass: "text-warning-700",
    subTextClass: "text-warning-700/80",
  },
  ERROR: {
    color: "danger",
    icon: AlertCircle,
    iconClass: "text-danger",
    cardClass: "border border-danger-200 bg-danger-50",
    textClass: "text-danger-700",
    subTextClass: "text-danger-700/80",
  },
};

export const ProtectedLayout = () => {
  const { t } = useTranslation("app");
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { customer, logout } = useSession();
  const navItems = customer?.isTestAccount
    ? allNavItems.filter((item) => item.url !== "/invoices")
    : allNavItems;
  const { data: paymentMethods, isLoading: isPaymentMethodsLoading } =
    usePaymentMethods();
  const { data: announcements } = useActiveAnnouncements();
  const { count: unpaidInvoicesCount, isLoading: isInvoicesLoading } =
    useUnpaidInvoices();

  const hasNoPaymentMethods = paymentMethods?.paymentMethods.length === 0;
  const shouldShowBillingWarning =
    !customer?.isTestAccount && hasNoPaymentMethods && !isPaymentMethodsLoading;
  const shouldShowInvoiceWarning =
    unpaidInvoicesCount > 0 && !isInvoicesLoading;
  const needsVerification =
    customer && !customer.emailVerified && !customer.phoneVerified;

  const userInitials = customer
    ? `${customer.firstName?.[0] ?? ""}${customer.lastName?.[0] ?? ""}`.toUpperCase()
    : "";

  const isItemActive = (url: string) =>
    pathname === url ||
    (url === "/dashboard" && pathname.startsWith("/orders"));

  return (
    <ProtectedRoute>
      <div className="min-h-screen bg-default-50 flex flex-col">
        {/* Top navbar */}
        <Navbar
          maxWidth="xl"
          isBordered
          classNames={{
            base: "bg-background",
            wrapper: "px-4 md:px-8",
          }}
        >
          <NavbarBrand as={Link} to="/dashboard" className="gap-0">
            <span className="text-lg font-bold tracking-tight text-foreground">
              {t("brand.shipping")}
            </span>
            <span className="ml-1 text-lg font-bold tracking-tight text-primary">
              TooEZ
            </span>
          </NavbarBrand>

          <NavbarContent justify="center" className="hidden md:flex gap-1">
            {navItems.map((item) => {
              const active = isItemActive(item.url);
              return (
                <NavbarItem key={item.url} isActive={active}>
                  <Link
                    to={item.url}
                    className={`flex items-center gap-1.5 rounded-medium px-3 py-2 text-small font-medium transition-colors ${
                      active
                        ? "text-primary bg-primary/10"
                        : "text-default-600 hover:text-foreground hover:bg-default-100"
                    }`}
                  >
                    {t(item.titleKey)}
                    {item.url === "/invoices" && unpaidInvoicesCount > 0 && (
                      <Chip
                        size="sm"
                        color="danger"
                        variant="flat"
                        className="h-5 min-w-5 px-1 text-tiny font-medium"
                      >
                        {unpaidInvoicesCount}
                      </Chip>
                    )}
                  </Link>
                </NavbarItem>
              );
            })}
          </NavbarContent>

          <NavbarContent justify="end" className="gap-1">
            <LanguageSwitcher />
            <button
              type="button"
              aria-label={t("notifications.label")}
              className="w-9 h-9 rounded-full grid place-items-center bg-default-100 hover:bg-default-200 text-default-700 transition-colors"
              title={t("notifications.title")}
            >
              <Bell className="h-4 w-4" />
            </button>
            <Dropdown
              placement="bottom-end"
              classNames={{ content: "min-w-[320px] p-2" }}
            >
              <DropdownTrigger>
                <Button
                  variant="light"
                  size="sm"
                  className="px-1 gap-2 rounded-full"
                  endContent={
                    <ChevronDown className="h-3.5 w-3.5 text-default-500" />
                  }
                  startContent={
                    <Avatar
                      name={userInitials}
                      size="sm"
                      classNames={{
                        base: "bg-primary",
                        name: "text-primary-foreground text-tiny font-semibold",
                      }}
                    />
                  }
                >
                  <span className="hidden md:inline text-small font-medium text-default-800">
                    {customer?.firstName}
                  </span>
                </Button>
              </DropdownTrigger>
              <DropdownMenu
                aria-label={t("accountMenu.ariaLabel")}
                itemClasses={{
                  base: "data-[hover=true]:bg-default-100 rounded-medium",
                }}
              >
                <DropdownItem
                  key="identity"
                  isReadOnly
                  className="opacity-100 cursor-default border-b border-divider mb-1 pb-3"
                  textValue="identity"
                >
                  <div className="flex items-center gap-3">
                    <Avatar
                      name={userInitials}
                      size="md"
                      classNames={{
                        base: "bg-primary",
                        name: "text-primary-foreground font-semibold",
                      }}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold truncate">
                        {customer?.firstName} {customer?.lastName}
                      </div>
                      <div className="text-tiny text-default-500 truncate">
                        {customer?.email ?? customer?.phone ?? ""}
                      </div>
                    </div>
                  </div>
                </DropdownItem>
                <DropdownItem
                  key="business"
                  isReadOnly
                  className={`opacity-100 cursor-default mb-1 ${customer?.businessAccount ? "" : "hidden"}`}
                  textValue="business"
                >
                  <div className="text-tiny font-semibold uppercase tracking-wider text-default-500 mb-1.5">
                    {t("accountMenu.business")}
                  </div>
                  <div className="flex items-center gap-2.5 rounded-medium bg-primary-50 px-3 py-2 text-primary-700">
                    <div className="flex h-7 w-7 items-center justify-center rounded-md bg-background text-primary text-tiny font-bold">
                      {customer?.businessAccount?.companyName
                        ?.slice(0, 2)
                        .toUpperCase() ?? ""}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-small font-semibold truncate">
                        {customer?.businessAccount?.companyName ?? ""}
                      </div>
                      <div className="text-tiny opacity-70">
                        {t("accountMenu.active")}
                      </div>
                    </div>
                  </div>
                </DropdownItem>
                <DropdownItem
                  key="settings"
                  startContent={<User className="h-4 w-4 text-default-500" />}
                  description={t("accountMenu.settingsDescription")}
                  onPress={() => navigate("/settings")}
                >
                  {t("accountMenu.settings")}
                </DropdownItem>
                <DropdownItem
                  key="vouchers"
                  startContent={<Ticket className="h-4 w-4 text-default-500" />}
                  description={t("accountMenu.vouchersDescription")}
                  onPress={() => navigate("/vouchers")}
                >
                  {t("accountMenu.vouchers")}
                </DropdownItem>
                <DropdownItem
                  key="billing"
                  startContent={
                    <CreditCard className="h-4 w-4 text-default-500" />
                  }
                  description={t("accountMenu.billingAddressDescription")}
                  onPress={() => navigate("/settings")}
                >
                  {t("accountMenu.billingAddress")}
                </DropdownItem>
                <DropdownItem
                  key="help"
                  startContent={
                    <LifeBuoy className="h-4 w-4 text-default-500" />
                  }
                  description={t("accountMenu.helpDescription")}
                  onPress={() => navigate("/help")}
                  showDivider
                >
                  {t("accountMenu.help")}
                </DropdownItem>
                <DropdownItem
                  key="logout"
                  color="danger"
                  className="text-danger"
                  startContent={<LogOut className="h-4 w-4" />}
                  onPress={logout}
                >
                  {t("accountMenu.signOut")}
                </DropdownItem>
              </DropdownMenu>
            </Dropdown>
          </NavbarContent>
        </Navbar>

        {/* Mobile nav strip */}
        <div className="md:hidden bg-background border-b border-divider">
          <div className="px-4 py-2 flex items-center gap-1 overflow-x-auto">
            {navItems.map((item) => {
              const active = isItemActive(item.url);
              return (
                <Link
                  key={item.url}
                  to={item.url}
                  className={`shrink-0 flex items-center gap-1.5 rounded-medium px-3 py-1.5 text-small font-medium whitespace-nowrap transition-colors ${
                    active
                      ? "text-primary bg-primary/10"
                      : "text-default-600 hover:text-foreground hover:bg-default-100"
                  }`}
                >
                  {t(item.titleKey)}
                  {item.url === "/invoices" && unpaidInvoicesCount > 0 && (
                    <Chip
                      size="sm"
                      color="danger"
                      variant="flat"
                      className="h-5 min-w-5 px-1 text-tiny font-medium"
                    >
                      {unpaidInvoicesCount}
                    </Chip>
                  )}
                </Link>
              );
            })}
          </div>
        </div>

        {/* Main */}
        <main className="flex-1">
          <div className="mx-auto max-w-6xl px-4 md:px-8 py-6 space-y-4 md:space-y-6">
            {customer?.isTestAccount && (
              <Card
                shadow="none"
                className="border border-warning-200 bg-warning-50"
              >
                <CardBody className="flex flex-row items-center gap-3">
                  <AlertTriangle className="h-5 w-5 shrink-0 text-warning-600" />
                  <p className="font-semibold text-warning-700">
                    {t("banners.testAccount")}
                  </p>
                </CardBody>
              </Card>
            )}

            {announcements?.map((announcement) => {
              const config = announcementConfig[announcement.type];
              const Icon = config.icon;
              return (
                <Card
                  key={announcement.id}
                  shadow="none"
                  className={config.cardClass}
                >
                  <CardBody className="p-4 md:p-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <div className="flex items-start sm:items-center gap-3">
                      <Icon
                        className={`h-6 w-6 shrink-0 mt-0.5 sm:mt-0 ${config.iconClass}`}
                      />
                      <div>
                        <p
                          className={`text-base md:text-lg font-semibold ${config.textClass}`}
                        >
                          {announcement.title}
                        </p>
                        <p className={`text-small ${config.subTextClass}`}>
                          {announcement.message}
                        </p>
                      </div>
                    </div>
                    {announcement.linkUrl && (
                      <Button
                        as="a"
                        href={announcement.linkUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        size="sm"
                        variant="flat"
                        color={config.color}
                        endContent={<ExternalLink className="h-4 w-4" />}
                      >
                        {announcement.linkText ?? t("banners.learnMore")}
                      </Button>
                    )}
                  </CardBody>
                </Card>
              );
            })}

            {shouldShowBillingWarning && (
              <Card
                shadow="none"
                className="border border-warning-200 bg-warning-50"
              >
                <CardBody className="p-4 md:p-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <div className="flex items-start sm:items-center gap-3">
                    <AlertTriangle className="h-6 w-6 shrink-0 mt-0.5 sm:mt-0 text-warning-600" />
                    <div>
                      <p className="text-base md:text-lg font-semibold text-warning-700">
                        {t("banners.billingTitle")}
                      </p>
                      <p className="text-small text-warning-700/80">
                        {t("banners.billingBody")}
                      </p>
                    </div>
                  </div>
                  <Button
                    as={Link}
                    to="/settings"
                    color="warning"
                    variant="flat"
                    size="sm"
                    className="w-full sm:w-auto"
                  >
                    {t("banners.billingAction")}
                  </Button>
                </CardBody>
              </Card>
            )}

            {shouldShowInvoiceWarning && (
              <Card
                shadow="none"
                className="border border-danger-200 bg-danger-50"
              >
                <CardBody className="p-4 md:p-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <div className="flex items-start sm:items-center gap-3">
                    <FileText className="h-6 w-6 shrink-0 mt-0.5 sm:mt-0 text-danger" />
                    <div>
                      <p className="text-base md:text-lg font-semibold text-danger-700">
                        {t("banners.invoiceTitle", {
                          count: unpaidInvoicesCount,
                        })}
                      </p>
                      <p className="text-small text-danger-700/80">
                        {t("banners.invoiceBody")}
                      </p>
                    </div>
                  </div>
                  <Button
                    as={Link}
                    to="/invoices"
                    color="danger"
                    variant="flat"
                    size="sm"
                    className="w-full sm:w-auto"
                  >
                    {t("banners.invoiceAction")}
                  </Button>
                </CardBody>
              </Card>
            )}

            {needsVerification && (
              <Card
                shadow="none"
                className="border border-primary/20 bg-primary/5"
              >
                <CardBody className="p-4 md:p-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <div className="flex items-start sm:items-center gap-3">
                    <ShieldAlert className="h-6 w-6 shrink-0 mt-0.5 sm:mt-0 text-primary" />
                    <div>
                      <p className="text-base md:text-lg font-semibold text-primary">
                        {t("banners.verifyTitle")}
                      </p>
                      <p className="text-small text-primary/80">
                        {t("banners.verifyBody")}
                      </p>
                    </div>
                  </div>
                  <Button
                    as={Link}
                    to="/settings"
                    color="primary"
                    variant="flat"
                    size="sm"
                    className="w-full sm:w-auto"
                  >
                    {t("banners.verifyAction")}
                  </Button>
                </CardBody>
              </Card>
            )}

            <Outlet />
          </div>
        </main>

        {/* Footer */}
        <footer className="bg-background border-t border-divider py-5">
          <div className="mx-auto max-w-6xl px-4 md:px-8">
            <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between text-small text-default-500">
              <div className="flex items-center gap-5">
                <a
                  href={marketingUrl("/privacy-policy")}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-foreground"
                >
                  {t("footer.privacy")}
                </a>
                <a
                  href={marketingUrl("/terms-of-use")}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-foreground"
                >
                  {t("footer.terms")}
                </a>
                <a
                  href={marketingUrl("/help")}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-foreground"
                >
                  {t("footer.help")}
                </a>
              </div>
              <span>
                {t("footer.copyright", { year: new Date().getFullYear() })}
              </span>
            </div>
          </div>
        </footer>
      </div>
    </ProtectedRoute>
  );
};
